import JSZip from 'jszip';
import { Project, Section, Subsection, Item, ItemStatus } from '../models/types';
// Use require for file-saver to fix module resolution issues
const FileSaver = require('file-saver');
const { saveAs } = FileSaver;

/**
 * Format dates safely for display
 * @param dateValue Any date value
 * @returns Formatted date string
 */
const formatDate = (dateValue: any): string => {
  if (!dateValue) return 'N/A';
  
  // If it's already a string, return it
  if (typeof dateValue === 'string') return dateValue;
  
  // If it's a Date object, format it
  if (dateValue instanceof Date) {
    try {
      return dateValue.toLocaleDateString();
    } catch (e) {
      return dateValue.toString();
    }
  }
  
  // Fall back to string conversion
  return String(dateValue);
};

// Template content for different file types
const TEMPLATES = {
  vision: 
`# Project Vision Statement

## Project Name
[Project Name]

## Vision
[Concise statement of the project's purpose and long-term goal]

## Mission
[How the project will achieve its vision]

## Business Objectives
1. [Primary business objective]
2. [Secondary business objective]
3. [Additional business objectives]

## Value Proposition
[The unique value this platform provides to users]

## Target Audience
[Primary audience segments]

## Stakeholders
[Key stakeholders and their interests]

## Success Metrics
[Measurable indicators that will determine the success of the project]
1. [Primary metric]
2. [Secondary metric]
3. [Additional metrics]

## Strategic Alignment
[How this project aligns with broader organizational goals]`,
  
  persona: 
`# User Persona: [Persona Name]

## Basic Information
- **Name:** [Fictional name]
- **Age:** [Age range]
- **Occupation:** [Job title]
- **Location:** [Geography]
- **Education:** [Education level]

## Characteristics
- **Technical Proficiency:** [Low/Medium/High]
- **Domain Knowledge:** [Low/Medium/High]
- **Usage Frequency:** [Daily/Weekly/Monthly]
- **Device Preference:** [Mobile/Desktop/Tablet/Multiple]

## Goals
1. [Primary goal]
2. [Secondary goal]
3. [Additional goals]

## Pain Points
1. [Key frustration]
2. [Secondary frustration]
3. [Additional frustrations]

## Motivations
- [What drives this user to use the platform]
- [Key motivational factors]

## Behavioral Patterns
- [How they typically interact with similar platforms]
- [Time of day/week they might use the platform]
- [Context of use (work, home, commuting, etc.)]

## Scenarios
- [Key scenario where this user would use the platform]
- [Additional usage scenarios]

## Quotes
- "[Representative quote that captures this user's perspective]"

## Influence
- **Decision Making Role:** [Decision maker/Influencer/User]
- **Purchasing Power:** [Low/Medium/High]

## Photo
[Optional representative photo]

## Notes
[Additional notes about this persona]`,
  
  journey: 
`# User Journey: [Journey Name]

## Overview
**Persona:** [Link to related persona]
**Goal:** [What the user is trying to accomplish]
**Context:** [Situation or context in which this journey occurs]

## Journey Stages

### 1. [Stage Name - e.g., Awareness/Discovery]
- **Touchpoints:**
  - [Where/how the user interacts with the system at this stage]
- **User Actions:**
  - [What the user does]
- **User Thoughts:**
  - [What the user thinks]
- **User Feelings:**
  - [How the user feels - positive/negative/neutral]
- **Opportunities:**
  - [How to improve this stage of the journey]

### 2. [Stage Name - e.g., Consideration/Research]
- **Touchpoints:**
  - [Where/how the user interacts with the system at this stage]
- **User Actions:**
  - [What the user does]
- **User Thoughts:**
  - [What the user thinks]
- **User Feelings:**
  - [How the user feels - positive/negative/neutral]
- **Opportunities:**
  - [How to improve this stage of the journey]

### 3. [Stage Name - e.g., Decision/Selection]
- **Touchpoints:**
  - [Where/how the user interacts with the system at this stage]
- **User Actions:**
  - [What the user does]
- **User Thoughts:**
  - [What the user thinks]
- **User Feelings:**
  - [How the user feels - positive/negative/neutral]
- **Opportunities:**
  - [How to improve this stage of the journey]

### 4. [Stage Name - e.g., Usage/Adoption]
- **Touchpoints:**
  - [Where/how the user interacts with the system at this stage]
- **User Actions:**
  - [What the user does]
- **User Thoughts:**
  - [What the user thinks]
- **User Feelings:**
  - [How the user feels - positive/negative/neutral]
- **Opportunities:**
  - [How to improve this stage of the journey]

### 5. [Stage Name - e.g., Loyalty/Advocacy]
- **Touchpoints:**
  - [Where/how the user interacts with the system at this stage]
- **User Actions:**
  - [What the user does]
- **User Thoughts:**
  - [What the user thinks]
- **User Feelings:**
  - [How the user feels - positive/negative/neutral]
- **Opportunities:**
  - [How to improve this stage of the journey]

## Pain Points
1. [Major pain point in the journey]
2. [Additional pain points]

## Key Metrics
- [Metrics to measure success/failure at each stage]
- [Overall journey metrics]

## Improvement Opportunities
- [High-level opportunities to improve this journey]
- [Priority improvements to implement]

## Journey Map Visualization
[Placeholder for journey map visualization - could be created with a tool or drawn]`,
  
  feature: 
`# Feature: [Feature Name]

## Feature ID
[Unique identifier, e.g., FT-001]

## Description
[Concise description of the feature]

## User Value
[How this feature provides value to users]

## Business Value
[How this feature supports business objectives]

## Category
[Feature category, e.g., Core/Supporting/Future]

## Priority
[Must Have/Should Have/Could Have/Won't Have]

## User Stories
- As a [type of user], I want [goal] so that [benefit]
- As a [type of user], I want [goal] so that [benefit]
- As a [type of user], I want [goal] so that [benefit]

## Acceptance Criteria
1. [Criterion 1]
2. [Criterion 2]
3. [Criterion 3]

## Technical Considerations
[Any technical constraints or considerations]

## Dependencies
[Features or components this feature depends on]

## Metrics
[How success of this feature will be measured]

## Design Mockups
[Links to design mockups or wireframes]

## Implementation Notes
[Notes for developers implementing this feature]

## Testing Requirements
[Special testing considerations]

## Security Considerations
[Security implications, if any]

## Accessibility Requirements
[Accessibility considerations]

## Future Enhancements
[Potential future improvements to this feature]

## Stakeholder Sign-off
- [ ] Product Owner
- [ ] Technical Lead
- [ ] UX Designer
- [ ] QA Lead`,
  
  entity: 
`# Entity: [Entity Name]

## Overview
**Entity ID:** [Unique entity identifier, e.g., ENT-001]
**Description:** [Brief description of what this entity represents]
**Domain:** [Business domain this entity belongs to]
**Category:** [Core/Supporting/Reference/etc.]

## Attributes

| Name | Type | Description | Required | Default | Constraints |
|------|------|-------------|----------|---------|-------------|
| id | [UUID] | Unique identifier | Yes | - | Primary key |
| [attribute1] | [data type] | [description] | [Yes/No] | [default value] | [constraints] |
| [attribute2] | [data type] | [description] | [Yes/No] | [default value] | [constraints] |
| [created_at] | [datetime] | Creation timestamp | Yes | Current time | - |
| [updated_at] | [datetime] | Last update timestamp | Yes | Current time | - |

## Relationships

| Relationship | Target Entity | Type | Cardinality | Description |
|--------------|--------------|------|-------------|-------------|
| [relationship1] | [target entity] | [One-to-One/One-to-Many/Many-to-Many] | [1:1, 1:N, N:M] | [description] |
| [relationship2] | [target entity] | [One-to-One/One-to-Many/Many-to-Many] | [1:1, 1:N, N:M] | [description] |

## Indices
| Name | Attributes | Type | Description |
|------|------------|------|-------------|
| [index1] | [attributes] | [Unique/Non-unique] | [description] |
| [index2] | [attributes] | [Unique/Non-unique] | [description] |

## Validation Rules
- [Validation rule 1]
- [Validation rule 2]

## Access Patterns
- [Common query pattern 1]
- [Common query pattern 2]

## Data Lifecycle
- **Creation:** [When/how instances are created]
- **Updates:** [When/how instances are updated]
- **Archival/Deletion:** [Policy for archiving or deleting data]

## Security Considerations
- **Classification:** [Public/Internal/Confidential/Restricted]
- **Encryption:** [Requirements for encryption at rest or in transit]
- **Access Control:** [Who can access this entity data]
- **Audit Requirements:** [Requirements for auditing changes]

## Performance Considerations
- [Expected volume]
- [Growth projections]
- [Caching strategy]

## Notes
[Additional notes about this entity]`,
  
  api: 
`openapi: 3.0.0
info:
  title: [API Name]
  description: |
    [Detailed API description]
  version: '1.0'
  contact:
    name: [Contact Name]
    email: [Contact Email]
  license:
    name: [License, e.g., "Proprietary" or "MIT"]
servers:
  - url: [Development Server URL]
    description: Development Server
  - url: [Staging Server URL]
    description: Staging Server
  - url: [Production Server URL]
    description: Production Server
tags:
  - name: [Tag1]
    description: [Tag1 Description]
  - name: [Tag2]
    description: [Tag2 Description]
paths:
  /[resource]:
    get:
      summary: [Operation summary]
      description: [Operation description]
      tags:
        - [Tag1]
      parameters:
        - name: [parameter name]
          in: [query/path/header]
          required: [true/false]
          description: [Parameter description]
          schema:
            type: [data type]
      responses:
        '200':
          description: [Success response description]
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/[ResourceSchema]'
        '400':
          description: Bad request
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '401':
          description: Unauthorized
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '404':
          description: Resource not found
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '500':
          description: Internal server error
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
    post:
      summary: [Create a new resource]
      description: [Creation operation description]
      tags:
        - [Tag1]
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/[ResourceInput]'
      responses:
        '201':
          description: [Resource created successfully]
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/[ResourceSchema]'
        '400':
          description: Bad request
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '401':
          description: Unauthorized
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '500':
          description: Internal server error
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
  /[resource]/{id}:
    get:
      summary: [Get resource by ID]
      description: [Retrieve single resource description]
      tags:
        - [Tag1]
      parameters:
        - name: id
          in: path
          required: true
          description: [Resource identifier]
          schema:
            type: string
            format: uuid
      responses:
        '200':
          description: [Success response description]
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/[ResourceSchema]'
        '404':
          description: Resource not found
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
    put:
      summary: [Update resource]
      description: [Update operation description]
      tags:
        - [Tag1]
      parameters:
        - name: id
          in: path
          required: true
          description: [Resource identifier]
          schema:
            type: string
            format: uuid
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/[ResourceInput]'
      responses:
        '200':
          description: [Success response description]
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/[ResourceSchema]'
        '400':
          description: Bad request
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
        '404':
          description: Resource not found
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
    delete:
      summary: [Delete resource]
      description: [Delete operation description]
      tags:
        - [Tag1]
      parameters:
        - name: id
          in: path
          required: true
          description: [Resource identifier]
          schema:
            type: string
            format: uuid
      responses:
        '204':
          description: No content (successful deletion)
        '404':
          description: Resource not found
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Error'
components:
  schemas:
    [ResourceSchema]:
      type: object
      properties:
        id:
          type: string
          format: uuid
          description: [Resource unique identifier]
        [property1]:
          type: [data type]
          description: [Property description]
        [property2]:
          type: [data type]
          description: [Property description]
        created_at:
          type: string
          format: date-time
          description: Resource creation timestamp
        updated_at:
          type: string
          format: date-time
          description: Resource last update timestamp
      required:
        - id
        - [required property]
        - created_at
        - updated_at
    [ResourceInput]:
      type: object
      properties:
        [property1]:
          type: [data type]
          description: [Property description]
        [property2]:
          type: [data type]
          description: [Property description]
      required:
        - [required property]
    Error:
      type: object
      properties:
        code:
          type: string
          description: Error code
        message:
          type: string
          description: Error message
        details:
          type: object
          description: Additional error details
  securitySchemes:
    BearerAuth:
      type: http
      scheme: bearer
      bearerFormat: JWT
    ApiKeyAuth:
      type: apiKey
      in: header
      name: X-API-Key
security:
  - BearerAuth: []
  # - ApiKeyAuth: []`,
  
  adr: 
`# Architecture Decision Record: [Title]

## ADR-[Number]: [Title]

### Status
[Proposed, Accepted, Deprecated, Superseded]

### Date
[Date created]

### Context
[Description of the issue/requirement that this decision addresses]

### Decision
[The architecture decision that was made]

### Rationale
[Justification for the decision, including benefits and drawbacks]

### Consequences
[Impact and implications of this decision]
- **Positive:**
  - [Positive consequence]
  - [Positive consequence]
- **Negative:**
  - [Negative consequence]
  - [Negative consequence]
- **Neutral:**
  - [Neutral consequence]
  - [Neutral consequence]

### Alternatives Considered
[Other options that were evaluated]
- **Alternative 1:**
  - [Description]
  - [Pros]
  - [Cons]
- **Alternative 2:**
  - [Description]
  - [Pros]
  - [Cons]

### Related Decisions
[Links to related ADRs]

### References
[Links to relevant documentation, articles, etc.]

### Compliance
[Regulatory or compliance considerations, if applicable]

### Implementation Notes
[Any special notes about implementing this decision]

### Evaluation
[How and when this decision will be evaluated]`,
  
  cicd: 
`# CI/CD Pipeline Configuration

## Pipeline Overview
**Pipeline Name:** [Pipeline Name]
**Description:** [Brief description of the pipeline purpose]
**Repository/Project:** [Source code repository]

## Pipeline Stages

### 1. Source Code Management
- **Source Repository:** [Repository URL]
- **Branch Strategy:**
  - Main/Production Branch: [branch name]
  - Development Branch: [branch name]
  - Feature Branch Pattern: [pattern, e.g., feature/*]
  - Release Branch Pattern: [pattern, e.g., release/*]
- **Triggers:**
  - [ ] Push to specific branches: [branches]
  - [ ] Pull Request creation/update
  - [ ] Manual trigger
  - [ ] Scheduled: [schedule]

### 2. Build
- **Environment:** [Build environment]
- **Dependencies:** [Dependency management approach]
- **Build Commands:**
  \`\`\`
  [Build command 1]
  [Build command 2]
  \`\`\`
- **Artifacts:**
  - [Artifact 1]
  - [Artifact 2]
- **Cache Configuration:**
  - [Cache paths and settings]

### 3. Test
- **Unit Tests:**
  - Framework: [Test framework]
  - Commands:
    \`\`\`
    [Test command 1]
    [Test command 2]
    \`\`\`
  - Coverage Thresholds: [Coverage requirements]
- **Integration Tests:**
  - Framework: [Test framework]
  - Environment: [Test environment]
  - Commands:
    \`\`\`
    [Test command]
    \`\`\`
- **End-to-End Tests:**
  - Framework: [Test framework]
  - Environment: [Test environment]
  - Commands:
    \`\`\`
    [Test command]
    \`\`\`
- **Security Scans:**
  - Tools: [Security scanning tools]
  - Configuration: [Scan configuration]
- **Performance Tests:**
  - Tools: [Performance testing tools]
  - Thresholds: [Performance requirements]

### 4. Code Quality
- **Static Analysis:**
  - Tools: [Static analysis tools]
  - Configuration: [Configuration files]
  - Quality Gates: [Quality requirements]
- **Linting:**
  - Tools: [Linting tools]
  - Configuration: [Configuration files]
- **Style Checks:**
  - Standards: [Coding standards]
  - Tools: [Style checking tools]

### 5. Deployment
- **Development Environment:**
  - Infrastructure: [Infrastructure details]
  - Deployment Strategy: [Blue/Green, Canary, etc.]
  - Commands:
    \`\`\`
    [Deployment commands]
    \`\`\`
  - Post-Deployment Validation:
    \`\`\`
    [Validation commands]
    \`\`\`

- **Staging Environment:**
  - Infrastructure: [Infrastructure details]
  - Deployment Strategy: [Blue/Green, Canary, etc.]
  - Approval Process: [Required approvals]
  - Commands:
    \`\`\`
    [Deployment commands]
    \`\`\`
  - Post-Deployment Validation:
    \`\`\`
    [Validation commands]
    \`\`\`

- **Production Environment:**
  - Infrastructure: [Infrastructure details]
  - Deployment Strategy: [Blue/Green, Canary, etc.]
  - Approval Process: [Required approvals]
  - Commands:
    \`\`\`
    [Deployment commands]
    \`\`\`
  - Post-Deployment Validation:
    \`\`\`
    [Validation commands]
    \`\`\`
  - Rollback Procedure:
    \`\`\`
    [Rollback commands]
    \`\`\`

### 6. Monitoring & Feedback
- **Monitoring Tools:** [Monitoring infrastructure]
- **Metrics Collection:** [Metrics to collect]
- **Alert Configuration:** [Alert thresholds and channels]
- **Feedback Loop:** [How monitoring data informs development]

## Pipeline Visualization
\`\`\`
[Pipeline diagram or ASCII representation]
\`\`\`

## Success Criteria
- [Criteria 1]
- [Criteria 2]

## Failure Handling
- [Failure scenario 1]: [Response]
- [Failure scenario 2]: [Response]

## Pipeline Metrics
- **Build Time Target:** [Target build duration]
- **Success Rate Target:** [Target success percentage]
- **Deployment Frequency:** [Expected deployment frequency]

## Security & Compliance
- **Credential Management:** [How credentials are managed]
- **Compliance Requirements:** [Relevant compliance considerations]
- **Audit Trail:** [Audit requirements]`,
  
  security: 
`# Security Architecture

## Overview
**Document ID:** [Document identifier]
**Version:** [Version number]
**Last Updated:** [Date]
**Author:** [Author name]

## Security Principles
- [Principle 1]
- [Principle 2]
- [Principle 3]

## Threat Model
### Assets
- [Asset 1]: [Description]
- [Asset 2]: [Description]

### Threat Actors
- [Threat Actor 1]: [Description and capabilities]
- [Threat Actor 2]: [Description and capabilities]

### Threats and Risks
- [Threat 1]: [Description and risk level]
- [Threat 2]: [Description and risk level]

## Security Controls

### Authentication
- **Mechanisms:** [Authentication mechanisms]
- **Policies:**
  - [Policy 1]
  - [Policy 2]
- **Implementation Details:** [Implementation specifics]

### Authorization
- **Access Control Model:** [RBAC, ABAC, etc.]
- **Roles/Permissions:** [Role definitions]
- **Implementation Details:** [Implementation specifics]

### Data Protection
- **Data Classification:**
  - [Classification 1]: [Description]
  - [Classification 2]: [Description]
- **Encryption:**
  - Data at Rest: [Approach]
  - Data in Transit: [Approach]
  - Data in Use: [Approach]
- **Data Masking:** [Approach for sensitive data]
- **Data Retention:** [Policies and implementation]

### Network Security
- **Network Segmentation:** [Approach]
- **Firewalls and Rules:** [Configuration approach]
- **API Security:** [Measures]
- **DDoS Protection:** [Measures]

### Application Security
- **Secure Development Practices:**
  - [Practice 1]
  - [Practice 2]
- **Input Validation:** [Approach]
- **Output Encoding:** [Approach]
- **Session Management:** [Approach]
- **Error Handling:** [Approach]
- **Dependency Management:** [Approach]

### Monitoring and Detection
- **Logging:** [Strategy and implementation]
- **Intrusion Detection:** [Approach]
- **Anomaly Detection:** [Approach]
- **Security Information and Event Management (SIEM):** [Implementation]

### Incident Response
- **Process Overview:** [Process description]
- **Roles and Responsibilities:** [Team structure]
- **Communication Plan:** [Communication procedures]
- **Recovery Procedures:** [Recovery approach]

## Compliance Requirements
- **Regulatory Requirements:**
  - [Requirement 1]: [Implementation approach]
  - [Requirement 2]: [Implementation approach]
- **Industry Standards:**
  - [Standard 1]: [Implementation approach]
  - [Standard 2]: [Implementation approach]

## Security Testing
- **Static Application Security Testing (SAST):** [Tools and process]
- **Dynamic Application Security Testing (DAST):** [Tools and process]
- **Penetration Testing:** [Frequency and scope]
- **Vulnerability Scanning:** [Tools and process]

## Security Operations
- **Patch Management:** [Process]
- **Configuration Management:** [Process]
- **Secrets Management:** [Process]
- **Access Reviews:** [Process and frequency]

## Security Architecture Diagram
\`\`\`
[Security architecture diagram or ASCII representation]
\`\`\`

## References
- [Reference 1]
- [Reference 2]

## Appendix
- [Additional security details]`,
  
  ai: 
`# AI Capabilities

## Overview
**Document ID:** [Document identifier]
**Version:** [Version number]
**Last Updated:** [Date]
**Author:** [Author name]

## AI Strategy
- **Goals:** [What the AI capabilities aim to achieve]
- **Principles:** [Guiding principles for AI implementation]
- **Success Metrics:** [How AI success will be measured]

## AI Capabilities

### [Capability 1]
- **Description:** [Detailed description]
- **Business Value:** [How this capability creates value]
- **User Experience:** [How users interact with this capability]
- **Technical Approach:** [Implementation approach]
- **Data Requirements:** [Data needed for this capability]
- **Performance Metrics:** [How performance is measured]

### [Capability 2]
- **Description:** [Detailed description]
- **Business Value:** [How this capability creates value]
- **User Experience:** [How users interact with this capability]
- **Technical Approach:** [Implementation approach]
- **Data Requirements:** [Data needed for this capability]
- **Performance Metrics:** [How performance is measured]

## Technical Implementation

### Model Selection
- **Selected Models:**
  - [Model 1]: [Purpose and justification]
  - [Model 2]: [Purpose and justification]
- **Evaluation Criteria:** [Criteria used to select models]
- **Alternatives Considered:** [Other models that were evaluated]

### Infrastructure
- **Hosting:** [Where models are hosted]
- **Serving:** [How model predictions are served]
- **Scaling:** [How the system scales to meet demand]
- **Performance Requirements:** [Latency, throughput, etc.]

### Integration
- **Integration Points:** [Where AI integrates with the system]
- **APIs:** [APIs for AI functionality]
- **Data Flow:** [How data flows to and from AI components]

### Data Pipeline
- **Data Sources:** [Where data comes from]
- **Data Preparation:** [How data is prepared]
- **Feature Engineering:** [How features are created]
- **Training Pipeline:** [How models are trained]
- **Evaluation Pipeline:** [How models are evaluated]
- **Deployment Pipeline:** [How models are deployed]

## Responsible AI

### Ethics
- **Ethical Principles:** [Guiding ethical principles]
- **Ethical Risks:** [Identified ethical risks]
- **Mitigation Strategies:** [How ethical risks are mitigated]

### Fairness and Bias
- **Fairness Metrics:** [How fairness is measured]
- **Bias Detection:** [How bias is detected]
- **Bias Mitigation:** [How bias is mitigated]

### Transparency
- **Explainability:** [How model decisions are explained]
- **Documentation:** [How models are documented]
- **User Communication:** [How AI capabilities are communicated to users]

### Privacy
- **Data Privacy:** [How privacy is protected]
- **Data Minimization:** [How data collection is minimized]
- **User Control:** [How users control their data]

### Governance
- **Oversight:** [How AI capabilities are overseen]
- **Review Process:** [How AI capabilities are reviewed]
- **Accountability:** [Who is accountable for AI capabilities]

## Monitoring and Maintenance

### Monitoring
- **Performance Monitoring:** [How performance is monitored]
- **Drift Detection:** [How data and concept drift are detected]
- **Alerting:** [When alerts are triggered]

### Maintenance
- **Model Retraining:** [When models are retrained]
- **Version Control:** [How models are versioned]
- **Rollback Procedures:** [How to roll back to previous versions]

## Development and Testing

### Development Process
- **Development Workflow:** [How AI capabilities are developed]
- **Collaboration:** [How teams collaborate]
- **Version Control:** [How code is versioned]

### Testing
- **Unit Testing:** [How components are tested]
- **Integration Testing:** [How the system is tested]
- **A/B Testing:** [How capabilities are tested in production]
- **User Testing:** [How capabilities are tested with users]

## Security
- **Data Security:** [How data is secured]
- **Model Security:** [How models are secured]
- **Access Control:** [Who can access AI capabilities]
- **Attack Vectors:** [Potential security threats]
- **Mitigations:** [How security threats are mitigated]

## Roadmap
- **Current State:** [Current implementation]
- **Short-term Goals:** [0-6 months]
- **Medium-term Goals:** [6-12 months]
- **Long-term Goals:** [12+ months]

## References
- [Reference 1]
- [Reference 2]

## Appendix
- [Additional details]`
};

/**
 * Function to generate project files based on a project's structure
 * @param project The project to export
 * @returns A JSZip object containing the project files
 */
export const generateProjectFiles = (project: Project): JSZip => {
  const zip = new JSZip();
  
  // Create folders and files based on project structure
  project.sections.forEach(section => {
    // Create section folders
    const sectionFolder = getSectionFolder(section);
    
    // Process subsections
    section.subsections.forEach(subsection => {
      // Create subsection folders
      const subsectionFolder = getSubsectionFolder(section, subsection);
      
      // Add templates for each subsection type
      const subsectionContent = getTemplateContent(section, subsection);
      if (subsectionContent) {
        zip.folder(subsectionFolder)?.file(`${getFileNameForSubsection(subsection)}.md`, subsectionContent);
      }
      
      // Add content for specific items
      subsection.items.forEach(item => {
        const itemContent = getItemContent(section, subsection, item);
        if (itemContent) {
          const fileName = getFileNameForItem(item);
          const fileExtension = getFileExtensionForItem(section, subsection);
          zip.folder(subsectionFolder)?.file(`${fileName}${fileExtension}`, itemContent);
        }
      });
    });
  });
  
  // Add README.md
  zip.file("README.md", generateReadme(project));
  
  return zip;
};

/**
 * Export the project as a zip file
 * @param project The project to export
 */
export const exportProject = async (project: Project): Promise<void> => {
  try {
    const zip = generateProjectFiles(project);
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `${project.name.replace(/\s+/g, '_').toLowerCase()}_export.zip`);
  } catch (error) {
    console.error("Error exporting project:", error);
    throw error;
  }
};

/**
 * Get the section folder name
 * @param section The section
 * @returns The folder name
 */
const getSectionFolder = (section: Section): string => {
  // Extract section number from name (e.g. "1. Requirements" -> "1-requirements")
  const match = section.name.match(/^(\d+)\.\s*(.+)$/);
  if (match) {
    const [, num, name] = match;
    return `${num}-${name.toLowerCase().replace(/\s+/g, '-')}`;
  }
  // Fallback if the section name doesn't match the expected format
  return section.name.toLowerCase().replace(/\s+/g, '-');
};

/**
 * Get the subsection folder path
 * @param section The parent section
 * @param subsection The subsection
 * @returns The folder path
 */
const getSubsectionFolder = (section: Section, subsection: Subsection): string => {
  const sectionFolder = getSectionFolder(section);
  
  // Extract subsection number from name (e.g. "1.1 Vision" -> "1.1-vision")
  const match = subsection.name.match(/^(\d+\.\d+)\s*(.+)$/);
  if (match) {
    const [, num, name] = match;
    return `${sectionFolder}/${num.replace('.', '-')}-${name.toLowerCase().replace(/\s+/g, '-')}`;
  }
  
  // Fallback
  return `${sectionFolder}/${subsection.name.toLowerCase().replace(/\s+/g, '-')}`;
};

/**
 * Get content based on section and subsection, combining item descriptions
 * @param section The section
 * @param subsection The subsection
 * @returns The combined content
 */
const getTemplateContent = (section: Section, subsection: Subsection): string | null => {
  // Extract section and subsection numbers for template matching
  const sectionMatch = section.name.match(/^(\d+)\./);
  const subsectionMatch = subsection.name.match(/^(\d+\.\d+)/);
  
  if (!sectionMatch || !subsectionMatch) return null;
  
  const sectionNum = parseInt(sectionMatch[1]);
  const subsectionFull = subsectionMatch[1];
  
  // Get base template
  let baseTemplate = '';
  if (sectionNum === 1) { // Requirements & Planning
    if (subsectionFull === '1.1') baseTemplate = TEMPLATES.vision;
    if (subsectionFull === '1.2') {
      if (subsection.name.includes('Persona')) baseTemplate = TEMPLATES.persona;
      if (subsection.name.includes('Journey')) baseTemplate = TEMPLATES.journey;
    }
  } else if (sectionNum === 2) { // Functional Architecture
    if (subsectionFull === '2.1') baseTemplate = TEMPLATES.feature;
    if (subsectionFull === '2.2') baseTemplate = TEMPLATES.entity;
    if (subsectionFull === '2.3') baseTemplate = TEMPLATES.api;
  } else if (sectionNum === 3) { // Technical Architecture
    if (subsectionFull === '3.1') baseTemplate = TEMPLATES.adr;
  } else if (sectionNum === 4) { // Cross Cutting Concerns
    if (subsectionFull === '4.1') baseTemplate = TEMPLATES.cicd;
    if (subsectionFull === '4.3') baseTemplate = TEMPLATES.security;
    if (subsectionFull === '4.6') baseTemplate = TEMPLATES.ai;
  }
  
  if (!baseTemplate) return null;
  
  // Now merge actual content from items if available
  let contentWithRealData = baseTemplate;
  
  // Get completed items with descriptions
  const completedItems = subsection.items.filter(
    item => item.status === ItemStatus.Completed && item.description.trim() !== ''
  );
  
  // Replace placeholder content with actual content where available
  completedItems.forEach(item => {
    const titleLower = item.title.toLowerCase();
    
    // Handle some common patterns in our templates
    if (titleLower.includes('vision')) {
      contentWithRealData = contentWithRealData.replace(/\[Concise statement of the project's purpose and long-term goal\]/g, item.description);
    } else if (titleLower.includes('mission')) {
      contentWithRealData = contentWithRealData.replace(/\[How the project will achieve its vision\]/g, item.description);
    } else if (titleLower.includes('objective') || titleLower.includes('goal')) {
      contentWithRealData = contentWithRealData.replace(/\[Primary business objective\]/g, item.description);
    } else if (titleLower.includes('proposition') || titleLower.includes('value')) {
      contentWithRealData = contentWithRealData.replace(/\[The unique value this platform provides to users\]/g, item.description);
    } else if (titleLower.includes('audience') || titleLower.includes('target')) {
      contentWithRealData = contentWithRealData.replace(/\[Primary audience segments\]/g, item.description);
    } else if (titleLower.includes('stakeholder')) {
      contentWithRealData = contentWithRealData.replace(/\[Key stakeholders and their interests\]/g, item.description);
    } else if (titleLower.includes('metric') || titleLower.includes('measure')) {
      contentWithRealData = contentWithRealData.replace(/\[Measurable indicators that will determine the success of the project\]/g, item.description);
    } else if (titleLower.includes('alignment') || titleLower.includes('strategic')) {
      contentWithRealData = contentWithRealData.replace(/\[How this project aligns with broader organizational goals\]/g, item.description);
    }
    
    // Handle generic placeholders by project name
    contentWithRealData = contentWithRealData.replace(/\[Project Name\]/g, section.name);
  });
  
  return contentWithRealData;
};

/**
 * Get file name for a subsection
 * @param subsection The subsection
 * @returns The file name
 */
const getFileNameForSubsection = (subsection: Subsection): string => {
  // Extract number from subsection name
  const match = subsection.name.match(/^(\d+\.\d+)\s*(.+)$/);
  if (match) {
    const [, num, name] = match;
    return `${name.toLowerCase().replace(/\s+/g, '-')}-template`;
  }
  
  return 'template';
};

/**
 * Get content for a specific item
 * @param section The parent section
 * @param subsection The parent subsection
 * @param item The item
 * @returns The item content
 */
const getItemContent = (section: Section, subsection: Subsection, item: Item): string | null => {
  // Only create individual files for completed items with substantial content
  if (item.status === ItemStatus.Completed && item.description && item.description.trim().length > 50) {
    // Create a standalone file with item title and description
    return `# ${item.title}

${item.description}

## Metadata
- **Status:** ${item.status.replace(/_/g, ' ').toLowerCase()}
- **Last Modified:** ${formatDate(item.lastModified)}
${item.isRequired ? '- **Required:** Yes\n' : ''}
${item.notes ? `\n## Notes\n${item.notes}\n` : ''}`;
  }
  
  return null;
};

/**
 * Get file name for an item
 * @param item The item
 * @returns The file name
 */
const getFileNameForItem = (item: Item): string => {
  return item.title.toLowerCase().replace(/\s+/g, '-');
};

/**
 * Get file extension for an item based on section and subsection
 * @param section The parent section
 * @param subsection The parent subsection
 * @returns The file extension
 */
const getFileExtensionForItem = (section: Section, subsection: Subsection): string => {
  // Check for API contracts which use YAML
  if (section.name.includes('Integration') && subsection.name.includes('API')) {
    return '.yaml';
  }
  
  // Default to markdown
  return '.md';
};

/**
 * Generate README content for the project
 * @param project The project
 * @returns The README content
 */
const generateReadme = (project: Project): string => {
  const completedItems = project.sections.reduce((count, section) => {
    return count + section.subsections.reduce((subCount, subsection) => {
      return subCount + subsection.items.filter(item => item.status === ItemStatus.Completed).length;
    }, 0);
  }, 0);
  
  const totalItems = project.sections.reduce((count, section) => {
    return count + section.subsections.reduce((subCount, subsection) => {
      return subCount + subsection.items.length;
    }, 0);
  }, 0);
  
  
  return `# ${project.name}

${project.description || ''}

## Project Status
- **Created:** ${formatDate(project.createdAt)}
- **Last Updated:** ${formatDate(project.updatedAt)}
- **Overall Progress:** ${(project.overallProgress * 100).toFixed(1)}%
- **Completed Items:** ${completedItems}/${totalItems}

## Folder Structure
This export follows a standardized project documentation structure:

1. Requirements & Planning - Vision, user analysis, market analysis
2. Functional Architecture - Features, data models, integration patterns
3. Technical Architecture - Application design, API strategy, frontend architecture
4. Cross-Cutting Concerns - DevOps, security, AI capabilities, etc.
5. Implementation Strategy - Development methodology, phased delivery, operations

## Sections
${project.sections.map(section => `- ${section.name} (${(section.completionPercentage * 100).toFixed(1)}% complete)`).join('\n')}

## Exported By
Requirements Tracker Application
`;
};
